import { Builder } from '@builder.io/sdk';
import { useRouter } from 'next/router';
import { type FC } from 'react';

import Awards from 'molecules/Awards';
import MarketoForm from 'molecules/Form';
import Heading from 'molecules/Heading';
import Icon from 'molecules/Icon';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import ScrollFadeSection from 'components/Animations/ScrollFadeSection';
import { HeroWithFormConfigProps } from 'components/Hero/config';

import { useBuilderReference } from 'utils/hooks/useBuilderReference';
import ClientOnly from 'utils/hooks/useClientOnly';
import { useIsDesktop } from 'utils/hooks/useIsDesktop';

const HeroForm: FC<HeroWithFormConfigProps> = props => {
  const { sectionProps, rest } = splitSectionProps(props);
  const { form, awardItems, eventDate, eventTime, isFormStyling = false, secondaryCta, ...headingProps } = rest;

  type FormType = { model?: string; id?: string; value?: { data?: FormReference } };
  const typedForm = form as FormType | undefined;

  const hasPreloadedForm = !!typedForm?.value?.data;
  const formRefData = useBuilderReference(
    !hasPreloadedForm ? typedForm?.model : undefined,
    !hasPreloadedForm ? typedForm?.id : undefined,
  );
  const formData = hasPreloadedForm ? typedForm?.value?.data : formRefData;
  // True when a form reference is configured for this hero (whether already
  // preloaded or yet to be fetched). Used to reserve vertical space for the
  // ClientOnly form wrapper so it doesn't push content down once the Marketo
  // form mounts post-hydration. PSI flagged /white-label-integration-platform/
  // mobile with CLS 0.323 caused exactly by this missing space reservation.
  const hasFormReference = hasPreloadedForm || !!typedForm?.id;

  const router = useRouter();
  const isEvents = router.pathname.startsWith('/events') || isFormStyling;

  const isDesktop = useIsDesktop();
  const isEditing = hasPreloadedForm ? Builder.isEditing : false;

  const shouldRenderInlineForm = !isDesktop && !!formData && (!isEditing || (isEditing && !isDesktop));

  const shouldRenderSideForm = isDesktop && !!formData && (!isEditing || (isEditing && isDesktop));

  const hasSecondaryCta = Boolean(secondaryCta?.text && secondaryCta?.url);

  const renderSecondaryCta = () => {
    if (!hasSecondaryCta) {
      return null;
    }

    return (
      <ScrollFadeSection delay={0.35} animateOnMount>
        <div className="flex items-center justify-start gap-1">
          <a
            href={secondaryCta?.url || '#'}
            target={secondaryCta?.openInNewTab ? '_blank' : '_self'}
            rel={secondaryCta?.openInNewTab ? 'noopener noreferrer' : undefined}
            className="group flex items-center gap-1.5 text-lg font-medium text-gray-300 transition-colors hover:text-gray-400"
          >
            <span>{secondaryCta?.text || 'Lorem ipsum'}</span>
            <Icon icon="arrow-up-right" size={20} className="transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </ScrollFadeSection>
    );
  };

  return (
    <Section {...sectionProps}>
      <div className="flex flex-col items-start justify-start gap-8 sm:gap-16 lg:flex-row lg:gap-[50px]">
        <div className="order-1 flex w-full flex-col gap-4 lg:w-1/2">
          <Heading
            {...headingProps}
            alignment="left"
            size="lg"
            as={headingProps.as || 'h1'}
            animateOnMount
            isForm={isFormStyling}
            afterDates={
              shouldRenderInlineForm && isEvents ? (
                <ClientOnly>
                  <div className="flex flex-col gap-3">
                    <MarketoForm formData={formData as FormReference} />
                    {renderSecondaryCta()}
                  </div>
                </ClientOnly>
              ) : null
            }
            eventDate={eventDate}
            eventTime={eventTime}
          />

          {!isEvents && hasFormReference && (
            <div className="min-h-[520px] lg:hidden">
              {shouldRenderInlineForm && (
                <ClientOnly>
                  <div className="flex flex-col gap-3">
                    <MarketoForm formData={formData as FormReference} />
                    {renderSecondaryCta()}
                  </div>
                </ClientOnly>
              )}
            </div>
          )}

          {awardItems && (
            <div className="hidden justify-start lg:flex">
              <Awards awardItems={awardItems} size="sm" />
            </div>
          )}
        </div>

        {shouldRenderSideForm && (
          <ClientOnly>
            <div className="order-3 w-full lg:w-1/2">
              <ScrollFadeSection delay={0.4} animateOnMount>
                <div className="flex flex-col gap-3">
                  <MarketoForm formData={formData as FormReference} />
                  {renderSecondaryCta()}
                </div>
              </ScrollFadeSection>
            </div>
          </ClientOnly>
        )}

        {awardItems && (
          <div className="order-2 w-full justify-start lg:hidden">
            <Awards awardItems={awardItems} size="sm" />
          </div>
        )}
      </div>
    </Section>
  );
};

export default HeroForm;
